import React, { useCallback, useContext, useMemo } from 'react';

import { Link } from 'react-router-dom';
import Button from 'kingpin/atoms/Button';
import BoardContext from 'contexts/BoardContext';
import EntityDefinitionsContext from 'contexts/EntityDefinitionsContext';
import useEntityLink from 'components/GlobalSearch/hooks/useEntityLink';
import isPerformanceBoard from 'isPerformanceBoard';
import { BonusOverviewContext } from 'components/DriverAX/providers/BonusOverviewProvider';

import SelectedEntityContext from '../../contexts/SelectedEntityContext';
import { UIVersionContext } from '../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import captureException from '../../../../services/captureException';
import ToastContext from '../../../../contexts/ToastContext';
import OpenPopupEntityAppContext from '../../../../contextProviders/SplashScreenProviders/MiscProviders/PopupEntityAppProvider/OpenPopupEntityAppContext';
import PerformanceBoardSlideOutContext from '../../contexts/PerformanceBoardSlideOutContext';
import Row from '../../../Common/Row';
import BonusPeriodsContext from '../../../../contexts/BonusPeriodsContext';
import AdjustMenu from './AdjustMenu';

const EntityAppLink = ({
  entity,
  selectedEntity,
}: {
  entity: EntityDetails.Entity;
  selectedEntity: string;
}) => {
  const link = useEntityLink({ entity, value: selectedEntity });

  if (!link) {
    return null;
  }

  return (
    <Link to={link} target="_blank">
      <Button size={'Medium'} type={'Secondary'} icon={'open-link'} />
    </Link>
  );
};

const V3EntityAppLink = ({
  entity,
  selectedEntity,
}: {
  entity: EntityDetails.Entity;
  selectedEntity: string;
}) => {
  const { showToast } = useContext(ToastContext);
  const { openEntityPopup } = useContext(OpenPopupEntityAppContext);
  const { entityDetailsApps } = useContext(EntityDefinitionsContext);
  const { refreshBoard } = useContext(PerformanceBoardSlideOutContext);
  const { selectedBonusPeriod } = useContext(BonusPeriodsContext);
  const { board } = useContext(BoardContext);
  const isSlideoutOverrideEnabled =
    isPerformanceBoard(board) && !!board.isSlideoutOverrideEnabled;
  const isOverrideButtonEnabled =
    isPerformanceBoard(board) &&
    board.isSlideoutOverrideButtonEnabled !== false;
  const { enterOverrideMode } = useContext(BonusOverviewContext);

  const app = useMemo(() => {
    return (
      entityDetailsApps.find(
        (a) => a.entityId === entity.id && a.id === entity.defaultAppId,
      ) ?? entityDetailsApps.find((a) => a.entityId === entity.id)
    );
  }, [entity.defaultAppId, entity.id, entityDetailsApps]);

  const { isBonusApp, scoringTab } = useMemo(() => {
    if (!app) {
      return {
        isBonusApp: false,
      };
    }

    const scoringTab = app.tabs.find(
      (t) => t.type === 'driver-bonus-program-report',
    );
    if (!scoringTab) {
      return {
        isBonusApp: false,
      };
    }

    return {
      isBonusApp: true,
      scoringTab,
    };
  }, [app]);

  const scoringTabId = useMemo(() => {
    if (!isBonusApp || !scoringTab) {
      return undefined;
    }

    return scoringTab.typeId;
  }, [isBonusApp, scoringTab]);

  const onEntityAppClosed = useMemo(() => {
    if (!isBonusApp) {
      return undefined;
    }

    return refreshBoard;
  }, [isBonusApp, refreshBoard]);

  const onClick = useCallback(() => {
    if (!app) {
      const e = new Error();
      e.name = `V3EntityAppLink: Entity app not found for "${entity.name}"`;
      captureException(e);
      showToast('Something went wrong');
      return;
    }

    openEntityPopup({
      app,
      entity,
      entityValue: selectedEntity,
      onEntityAppClosed,
      selectedBonusPeriodId: selectedBonusPeriod
        ? selectedBonusPeriod.id
        : undefined,
    });
  }, [
    app,
    openEntityPopup,
    entity,
    selectedEntity,
    onEntityAppClosed,
    selectedBonusPeriod,
    showToast,
  ]);

  const onOverrideClicked = useCallback(() => {
    if (!selectedBonusPeriod) {
      return;
    }

    // With the board setting on, transform the slideout in place instead of
    // navigating to the entity-app popup. BoardBonusOverviewProvider has mounted
    // the override state provider for this bonus app, so enterOverrideMode drives
    // the in-slideout override view + header.
    if (isSlideoutOverrideEnabled) {
      enterOverrideMode();
      return;
    }

    if (!app) {
      const e = new Error();
      e.name = `V3EntityAppLink: Entity app not found for "${entity.name}"`;
      captureException(e);
      showToast('Something went wrong');
      return;
    }

    openEntityPopup({
      app,
      entity,
      entityValue: selectedEntity,
      initialTabId: scoringTabId,
      onEntityAppClosed,
      overrideMode: true,
      selectedBonusPeriodId: selectedBonusPeriod.id,
    });
  }, [
    app,
    entity,
    onEntityAppClosed,
    openEntityPopup,
    scoringTabId,
    selectedBonusPeriod,
    selectedEntity,
    showToast,
    isSlideoutOverrideEnabled,
    enterOverrideMode,
  ]);

  return (
    <Row style={{ gap: 8 }} centerAlign>
      <AdjustMenu
        isOverrideEnabled={isOverrideButtonEnabled}
        onOverrideClicked={onOverrideClicked}
      />
      <Button
        size={'Medium'}
        type={'Secondary'}
        icon={'open-link'}
        testId={'open-entity-app-link'}
        onClick={onClick}
      />
    </Row>
  );
};

/**
 * This is how an entity app gets popped up from a performance board
 */
const Gate = () => {
  const { isV3 } = useContext(UIVersionContext);
  const { selectedEntity, entityField } = useContext(SelectedEntityContext);
  const { entityDefinitions } = useContext(EntityDefinitionsContext);

  const entityDefinition = useMemo<EntityDetails.Entity | undefined>(
    () => entityDefinitions.find((d) => d.primaryField === entityField),
    [entityDefinitions, entityField],
  );

  if (!entityDefinition || !selectedEntity) {
    return null;
  }

  if (isV3) {
    return (
      <V3EntityAppLink
        entity={entityDefinition}
        selectedEntity={selectedEntity}
      />
    );
  }

  return (
    <EntityAppLink entity={entityDefinition} selectedEntity={selectedEntity} />
  );
};

export default Gate;
